import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { Upload, MessageSquare, Loader2, CheckCircle, AlertCircle, Sparkles } from 'lucide-react';
import axios from 'axios';
import './App.css';

const API_URL = 'https://job-matcher-ai-production.up.railway.app';

// ─── Translations ────────────────────────────────────────────────────────────
const t = {
  da: {
    banner: '⚠️ Dette er en prototype - data og funktioner er under udvikling',
    badge_danish: '🇩🇰 100% Danskejet',
    badge_gdpr: '🔒 100% GDPR compliant',
    badge_jobs: (n) => `💼 ${n.toLocaleString('da-DK')} ledige jobs`,
    step1_title: 'Find dit næste job med AI',
    step1_sub: 'Upload dit CV eller skriv din profil med hjælp fra en AI agent, så matcher vi dig med relevante stillinger',
    tab_upload: '📄 Upload CV',
    tab_chat: '✍️ Skriv profil',
    upload_label: 'Træk din CV-fil hertil eller klik for at vælge',
    upload_hint: 'PDF eller DOCX, maks 5 MB',
    chat_placeholder: 'Skriv dit svar...',
    send: 'Send',
    next: 'Næste →',
    step2_title: '💬 Opdatér din profil med AI',
    your_profile: 'Din profil:',
    edit_profile: 'Rediger din profil:',
    save_changes: 'Gem ændringer',
    find_jobs: 'Søg jobs nu',
    give_feedback: 'Giv mig feedback',
    edit_btn: 'Rediger profil',
    cancel: 'Annuller',
    chat_placeholder2: 'Skriv din besked...',
    update_from_chat: '✨ Opdater profil baseret på chat',
    matching_title: 'Matcher din profil',
    matching_sub: (n) => `Søger gennem ${n.toLocaleString('da-DK')} stillinger...`,
    matching_done: 'Fandt dine bedste matches!',
    step3_title: '🎯 Dine job matches',
    refine_title: 'Ikke tilfreds? Opdatér din søgning',
    refine_hint: 'Fx: "Jobs i København", "Gerne deltid", "Remote muligheder"',
    refine_placeholder: 'Beskriv hvad du leder efter...',
    refine_btn: 'Opdatér søgning',
    see_job: 'Se jobopslag',
    write_application: 'Skriv ansøgning',
    why_these: 'Hvorfor disse jobs passer til dig:',
    back_to_profile: '← Tilbage til profil',
    step4_title: '✍️ Din motiverede ansøgning',
    back_to_jobs: '← Tilbage til jobs',
    copy_application: 'Kopier ansøgning',
    copied: 'Ansøgning kopieret! 📋',
    error_upload: 'Fejl ved upload af CV. Prøv igen.',
    error_chat: 'Fejl ved chat. Prøv igen.',
    error_jobs: 'Fejl ved jobsøgning. Prøv igen.',
    error_refine: 'Fejl ved opdatering af søgning. Prøv igen.',
    error_letter: 'Fejl ved generering af ansøgning. Prøv igen.',
    error_feedback: 'Fejl ved feedback. Prøv igen.',
    error_update: 'Fejl ved opdatering af profil. Prøv igen.',
    profile_updated: '✅ Profil opdateret baseret på chat!',
    chat_empty: 'Stil spørgsmål eller få feedback på din profil',
    footer_privacy: 'Privatlivspolitik',
    footer_contact: 'Kontakt',
    footer_about: 'Om jobchat',
    lang_toggle: 'English',
    first_chat_msg: 'Hej! Lad os oprette din profil sammen. Lad os starte med det vigtigste: Hvad er du god til? Fortæl mig om dine vigtigste kompetencer eller erfaring.',
  },
  en: {
    banner: '⚠️ This is a prototype - data and features are under development',
    badge_danish: '🇩🇰 100% Danish owned',
    badge_gdpr: '🔒 100% GDPR compliant',
    badge_jobs: (n) => `💼 ${n.toLocaleString('en-DK')} open positions`,
    step1_title: 'Find your next job with AI',
    step1_sub: 'Upload your CV or write your profile with help from an AI agent, and we\'ll match you with relevant positions',
    tab_upload: '📄 Upload CV',
    tab_chat: '✍️ Write profile',
    upload_label: 'Drag your CV here or click to select',
    upload_hint: 'PDF or DOCX, max 5 MB',
    chat_placeholder: 'Write your answer...',
    send: 'Send',
    next: 'Next →',
    step2_title: '💬 Update your profile with AI',
    your_profile: 'Your profile:',
    edit_profile: 'Edit your profile:',
    save_changes: 'Save changes',
    find_jobs: 'Search jobs now',
    give_feedback: 'Give me feedback',
    edit_btn: 'Edit profile',
    cancel: 'Cancel',
    chat_placeholder2: 'Write your message...',
    update_from_chat: '✨ Update profile based on chat',
    matching_title: 'Matching your profile',
    matching_sub: (n) => `Searching through ${n.toLocaleString('en-DK')} positions...`,
    matching_done: 'Found your best matches!',
    step3_title: '🎯 Your job matches',
    refine_title: 'Not satisfied? Refine your search',
    refine_hint: 'E.g. "Jobs in Copenhagen", "Part time", "Remote options"',
    refine_placeholder: 'Describe what you\'re looking for...',
    refine_btn: 'Update search',
    see_job: 'See job posting',
    write_application: 'Write application',
    why_these: 'Why these jobs suit you:',
    back_to_profile: '← Back to profile',
    step4_title: '✍️ Your motivation letter',
    back_to_jobs: '← Back to jobs',
    copy_application: 'Copy application',
    copied: 'Application copied! 📋',
    error_upload: 'Error uploading CV. Please try again.',
    error_chat: 'Chat error. Please try again.',
    error_jobs: 'Error searching jobs. Please try again.',
    error_refine: 'Error updating search. Please try again.',
    error_letter: 'Error generating application. Please try again.',
    error_feedback: 'Error getting feedback. Please try again.',
    error_update: 'Error updating profile. Please try again.',
    profile_updated: '✅ Profile updated based on chat!',
    chat_empty: 'Ask questions or get feedback on your profile',
    footer_privacy: 'Privacy policy',
    footer_contact: 'Contact',
    footer_about: 'About jobchat',
    lang_toggle: 'Dansk',
    first_chat_msg: 'Hi! Let\'s create your profile together. Let\'s start with the most important thing: What are you good at? Tell me about your main skills or experience.',
  }
};

// ─── Privacy Policy Page ──────────────────────────────────────────────────────
function PrivacyPage() {
  return (
    <div className="static-page">
      <div className="static-page-wrap">
        <Link to="/" className="back-link">← Tilbage til jobchat.dk</Link>
        <p className="eyebrow">JobChat.dk</p>
        <h1>Privatlivspolitik</h1>
        <p className="meta">Sidst opdateret: juni 2026</p>

        <section>
          <h2>Dataansvarlig</h2>
          <p>Den dataansvarlige for behandlingen af personoplysninger på jobchat.dk er:</p>
          <div className="callout">
            <strong>Jobnest</strong>
            CVR-nr. 46335635<br />
            Kontakt: <a href="mailto:sb@jobchat.dk">sb@jobchat.dk</a>
          </div>
        </section>

        <section>
          <h2>Hvilke oplysninger behandler vi</h2>
          <p>Når du bruger JobChat til at finde relevante job, beder vi dig om at beskrive din baggrund, dine kompetencer og dine jobønsker — fx i form af en CV-tekst eller en fri beskrivelse. Disse oplysninger kan indeholde personoplysninger som navn, uddannelse, erhvervserfaring og kontaktoplysninger.</p>
          <p>Vi behandler udelukkende de oplysninger, du selv vælger at dele med os i forbindelse med en søgning.</p>
        </section>

        <section>
          <h2>Formål og retsgrundlag</h2>
          <p>Vi behandler dine oplysninger for at matche din profil med relevante jobopslag i vores database. Retsgrundlaget er dit samtykke, som du afgiver ved at indtaste og indsende dine oplysninger, jf. databeskyttelsesforordningen (GDPR) artikel 6, stk. 1, litra a.</p>
        </section>

        <section>
          <h2>Opbevaring og sletning</h2>
          <p>Dine oplysninger gemmes udelukkende i din aktive session — dvs. så længe du har browserfanen åben og benytter tjenesten. Når du afslutter sessionen eller lukker browseren, slettes alle oplysninger automatisk. Vi gemmer ingen personoplysninger i en database eller på anden vedvarende lagring.</p>
        </section>

        <section>
          <h2>Deling med tredjeparter</h2>
          <p>For at foretage den semantiske matching sendes din profilbeskrivelse til <strong>OpenAI</strong> (OpenAI, L.L.C., USA), som behandler teksten for at generere et søgeresultat. OpenAI fungerer som vores databehandler og må ikke anvende dine oplysninger til egne formål.</p>
          <p>Overførslen til USA sker på grundlag af EU-Kommissionens standardkontraktbestemmelser (SCC). Du kan læse mere om OpenAIs databeskyttelse på <a href="https://openai.com/policies/privacy-policy" target="_blank" rel="noopener noreferrer">openai.com/policies/privacy-policy</a>.</p>
          <p>Vi deler ikke dine oplysninger med andre tredjeparter.</p>
        </section>

        <section>
          <h2>Dine rettigheder</h2>
          <p>Du har til enhver tid ret til at få indsigt i de oplysninger, vi behandler om dig, samt ret til berigtigelse, sletning, begrænsning af behandling og dataportabilitet. Da vi ikke gemmer oplysninger efter sessionen er afsluttet, er dine data i praksis allerede slettet, når du forlader sitet.</p>
          <p>Hvis du har spørgsmål til vores behandling af dine oplysninger, er du velkommen til at kontakte os på <a href="mailto:sb@jobchat.dk">sb@jobchat.dk</a>.</p>
          <p>Du har også ret til at indgive en klage til <strong>Datatilsynet</strong>, Borgergade 28, 1300 København K, <a href="https://www.datatilsynet.dk" target="_blank" rel="noopener noreferrer">datatilsynet.dk</a>.</p>
        </section>

        <section>
          <h2>Cookies</h2>
          <p>JobChat.dk anvender udelukkende teknisk nødvendige cookies til sessionsstyring. Vi anvender ikke tracking- eller markedsføringscookies, og der indsamles ikke statistik om din adfærd på sitet.</p>
        </section>
      </div>
    </div>
  );
}

// ─── About Page ───────────────────────────────────────────────────────────────
function AboutPage() {
  return (
    <div className="static-page">
      <div className="static-page-wrap">
        <Link to="/" className="back-link">← Tilbage til jobchat.dk</Link>

        <section>
          <h2>Om os</h2>
          <p>Jobchat er en dansk jobservice som gør det simpelt at finde det rigtige job ved hjælp af AI. Vi samler alle online jobs ét sted og lader en AI-assistent matche din profil med det helt rigtige job. Nemt og enkelt, og med fokus på datasikkerhed.</p>
        </section>

        <section>
          <h2>Sådan virker det</h2>
          <p>Jobchat matcher dit CV eller den profil du har indtastet ved hjælp af en semantisk søgning. Det betyder at Jobchat læser alle jobannoncer igennem og analyserer hvor godt din profil vil passe ind i det specifikke job. Det giver et langt bedre resultat end ved traditionelle jobsøgningsportaler og muligheden for at hjælpe dig med en passende ansøgningstekst.</p>
        </section>

        <section>
          <h2>For alle — gratis</h2>
          <p>For Jobchat handler det om at give flest mulige jobsøgende den bedste hjælp og spare frustrationerne. Og at få flere i job. Derfor er Jobchat helt gratis for den jobsøgende!</p>
        </section>

        <section>
          <h2>Til kommuner og A-kasser</h2>
          <p>Er du en kommune eller A-kasse som vil tilbyde servicen til borgere eller medlemmer, så skriv til <a href="mailto:sb@jobchat.dk">sb@jobchat.dk</a> for yderligere info.</p>
        </section>

        <div className="about-cta">
          <Link to="/" className="cta-btn">Find dit næste job →</Link>
        </div>
      </div>
    </div>
  );
}

// ─── Loading Overlay ──────────────────────────────────────────────────────────
function MatchingOverlay({ jobCount, lang }) {
  const tr = t[lang];
  const [progress, setProgress] = useState(0);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const duration = 4000;
    const interval = 50;
    const steps = duration / interval;
    let current = 0;
    const timer = setInterval(() => {
      current++;
      const pct = Math.min(100, Math.round((current / steps) * 100));
      setProgress(pct);
      if (pct >= 100) {
        clearInterval(timer);
        setDone(true);
      }
    }, interval);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="matching-overlay">
      <div className="matching-box">
        {done
          ? <CheckCircle size={48} className="matching-icon done" />
          : <Loader2 size={48} className="matching-icon spinner" />
        }
        <h3>{tr.matching_title}</h3>
        <p>{tr.matching_sub(jobCount)}</p>
        <div className="matching-progress-track">
          <div className="matching-progress-bar" style={{ width: `${progress}%` }} />
        </div>
        <span className="matching-pct">{progress}%</span>
      </div>
    </div>
  );
}

// ─── Main App ─────────────────────────────────────────────────────────────────
function MainApp() {
  const [lang, setLang] = useState('da');
  const tr = t[lang];

  const [step, setStep] = useState(1);
  const [profile, setProfile] = useState('');
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [matches, setMatches] = useState([]);
  const [explanations, setExplanations] = useState('');
  const [selectedJob, setSelectedJob] = useState(null);
  const [motivationLetter, setMotivationLetter] = useState('');
  const [loading, setLoading] = useState(false);
  const [matching, setMatching] = useState(false);
  const [error, setError] = useState('');
  const [refinementInput, setRefinementInput] = useState('');
  const [showRefinement, setShowRefinement] = useState(false);
  const [showProfileEdit, setShowProfileEdit] = useState(false);
  const [activeTab, setActiveTab] = useState('upload');
  const [jobCount, setJobCount] = useState(0);
  const [profileChatMessages, setProfileChatMessages] = useState([
    { role: 'assistant', content: tr.first_chat_msg }
  ]);

  const fileInputRef = useRef(null);

  useEffect(() => {
    axios.get(`${API_URL}/job-count`)
      .then(res => setJobCount(res.data.count))
      .catch(() => setJobCount(0));
  }, []);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLoading(true);
    setError('');
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await axios.post(`${API_URL}/upload-cv`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setProfile(response.data.text);
      setStep(2);
    } catch (err) {
      setError(tr.error_upload);
    } finally {
      setLoading(false);
    }
  };

  const handleProfileChatSubmit = async () => {
    if (!chatInput.trim()) return;
    const userMessage = chatInput;
    setProfileChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setChatInput('');
    setLoading(true);
    setError('');
    try {
      const response = await axios.post(`${API_URL}/chat-profile`, {
        message: userMessage,
        profile: profile,
        system_prompt: "Du er en jobvejleder som skal guide en jobsøger gennem at oprette en simpel profil."
      });
      setProfileChatMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
      setProfile(prev => prev + '\n' + userMessage);
    } catch (err) {
      setError(tr.error_chat);
    } finally {
      setLoading(false);
    }
  };

  const handleChatSubmit = async () => {
    if (!chatInput.trim()) return;
    const userMessage = chatInput;
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setChatInput('');
    setLoading(true);
    setError('');
    try {
      const response = await axios.post(`${API_URL}/chat-profile`, { message: userMessage, profile });
      setChatMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (err) {
      setError(tr.error_chat);
    } finally {
      setLoading(false);
    }
  };

  const handleGiveFeedback = async () => {
    const feedbackMessage = "Giv mig kort feedback på min profil (2-3 linjer)";
    setChatMessages(prev => [...prev, { role: 'user', content: feedbackMessage }]);
    setLoading(true);
    setError('');
    try {
      const response = await axios.post(`${API_URL}/chat-profile`, { message: feedbackMessage, profile });
      setChatMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (err) {
      setError(tr.error_feedback);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfileFromChat = async () => {
    if (chatMessages.length === 0) return;
    setLoading(true);
    setError('');
    try {
      const chatHistory = chatMessages.map(msg => `${msg.role}: ${msg.content}`).join('\n\n');
      const response = await axios.post(`${API_URL}/chat-profile`, {
        message: `Baseret på denne samtale, generer en opdateret og forbedret profil:\n\n${chatHistory}\n\nGenerer kun den opdaterede profil, ingen forklaring.`,
        profile
      });
      setProfile(response.data.response);
      setChatMessages([]);
      alert(tr.profile_updated);
    } catch (err) {
      setError(tr.error_update);
    } finally {
      setLoading(false);
    }
  };

  const handleFindJobs = async () => {
    setMatching(true);
    setError('');
    try {
      const response = await axios.post(`${API_URL}/match-jobs`, { profile, top_k: 10 });
      // Let progress bar finish before showing results
      setTimeout(() => {
        setMatches(response.data.matches);
        setExplanations(response.data.explanations);
        setMatching(false);
        setStep(3);
        setShowRefinement(true);
      }, 4200);
    } catch (err) {
      setError(tr.error_jobs);
      setMatching(false);
    }
  };

  const handleRefineSearch = async () => {
    if (!refinementInput.trim()) return;
    setLoading(true);
    setError('');
    const updatedProfile = `${profile}\n\nPræferencer: ${refinementInput}`;
    setProfile(updatedProfile);
    try {
      const response = await axios.post(`${API_URL}/refine-jobs`, { profile: updatedProfile, top_k: 10 });
      setMatches(response.data.matches);
      setExplanations(response.data.explanations);
      setRefinementInput('');
    } catch (err) {
      setError(tr.error_refine);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateLetter = async (job) => {
    setSelectedJob(job);
    setLoading(true);
    setError('');
    try {
      const response = await axios.post(`${API_URL}/generate-motivation-letter`, {
        profile,
        job_description: job.description,
        company_name: job.company
      });
      setMotivationLetter(response.data.letter);
      setStep(4);
    } catch (err) {
      setError(tr.error_letter);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="App">
      {matching && <MatchingOverlay jobCount={jobCount} lang={lang} />}

      <header className="app-header">
        <div className="header-content">
          <Link to="/" className="logo-link" onClick={() => { setStep(1); setProfile(''); setChatMessages([]); setMatches([]); setError(''); }}>
            <h1>🎯 jobchat.dk</h1>
          </Link>
          <div className="header-badges">
            <span className="badge">{tr.badge_danish}</span>
            <span className="badge">{tr.badge_gdpr}</span>
            {jobCount > 0 && <span className="badge badge-jobs">{tr.badge_jobs(jobCount)}</span>}
          </div>
          <button className="lang-toggle" onClick={() => setLang(l => l === 'da' ? 'en' : 'da')}>
            {tr.lang_toggle}
          </button>
        </div>
      </header>

      <div className="container">
        {error && (
          <div className="error-message">
            <AlertCircle size={20} />
            <span>{error}</span>
          </div>
        )}

        {/* Step 1 */}
        {step === 1 && (
          <div className="step-container">
            <h2>{tr.step1_title}</h2>
            <p className="subtitle">{tr.step1_sub}</p>

            <div className="tabs">
              <button
                className={`tab ${activeTab === 'upload' ? 'active' : ''}`}
                onClick={() => { setActiveTab('upload'); fileInputRef.current && fileInputRef.current.click(); }}
              >
                {tr.tab_upload}
              </button>
              <button
                className={`tab ${activeTab === 'chat' ? 'active' : ''}`}
                onClick={() => setActiveTab('chat')}
              >
                {tr.tab_chat}
              </button>
            </div>

            {activeTab === 'upload' && (
              <div className="upload-section">
                <label
                  className="file-upload-btn"
                  onClick={() => fileInputRef.current && fileInputRef.current.click()}
                >
                  <Upload size={24} />
                  <span>{tr.upload_label}</span>
                  <p style={{ fontSize: '14px', opacity: 0.7, marginTop: '0.5rem' }}>{tr.upload_hint}</p>
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.docx,.txt"
                  onChange={handleFileUpload}
                  disabled={loading}
                  style={{ display: 'none' }}
                />
              </div>
            )}

            {activeTab === 'chat' && (
              <div className="chat-container">
                <div className="chat-messages">
                  {profileChatMessages.map((msg, idx) => (
                    <div key={idx} className={`chat-message ${msg.role}`}>
                      <div className="message-content">{msg.content}</div>
                    </div>
                  ))}
                  {loading && <div className="chat-message assistant"><Loader2 className="spinner" size={20} /></div>}
                </div>
                <div className="chat-input-container">
                  <input
                    type="text"
                    placeholder={tr.chat_placeholder}
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleProfileChatSubmit()}
                    disabled={loading}
                  />
                  <button onClick={handleProfileChatSubmit} disabled={!chatInput.trim() || loading}>{tr.send}</button>
                </div>
                <button
                  className="primary-btn"
                  onClick={() => profile.trim() && setStep(2)}
                  style={{ marginTop: '1rem', width: '100%' }}
                  disabled={!profile.trim() || loading}
                >
                  {tr.next}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Step 2 */}
        {step === 2 && (
          <div className="step-container">
            <h2>{tr.step2_title}</h2>
            {!showProfileEdit ? (
              <div className="profile-preview">
                <h3>{tr.your_profile}</h3>
                <p>{profile.substring(0, 300)}...</p>
              </div>
            ) : (
              <div className="profile-edit-section">
                <h3 style={{ color: '#FFFFFF', marginBottom: '1rem', fontSize: '18px' }}>{tr.edit_profile}</h3>
                <textarea className="profile-input" value={profile} onChange={(e) => setProfile(e.target.value)} rows={15} style={{ marginBottom: '1rem' }} />
                <button className="secondary-btn" onClick={() => setShowProfileEdit(false)}>{tr.save_changes}</button>
              </div>
            )}
            <div className="action-buttons">
              <button className="primary-btn" onClick={handleFindJobs} disabled={loading || matching}>
                {loading ? <Loader2 className="spinner" size={20} /> : tr.find_jobs}
              </button>
              <button className="primary-btn" onClick={handleGiveFeedback} disabled={loading}>{tr.give_feedback}</button>
              <button className="secondary-btn" onClick={() => setShowProfileEdit(!showProfileEdit)}>
                {showProfileEdit ? tr.cancel : tr.edit_btn}
              </button>
            </div>
            <div className="chat-container">
              <div className="chat-messages">
                {chatMessages.length === 0 && (
                  <div className="chat-placeholder">
                    <MessageSquare size={48} />
                    <p>{tr.chat_empty}</p>
                  </div>
                )}
                {chatMessages.map((msg, idx) => (
                  <div key={idx} className={`chat-message ${msg.role}`}>
                    <div className="message-content">{msg.content}</div>
                  </div>
                ))}
                {loading && <div className="chat-message assistant"><Loader2 className="spinner" size={20} /></div>}
              </div>
              <div className="chat-input-container">
                <input
                  type="text"
                  placeholder={tr.chat_placeholder2}
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleChatSubmit()}
                  disabled={loading}
                />
                <button onClick={handleChatSubmit} disabled={!chatInput.trim() || loading}>{tr.send}</button>
              </div>
              {chatMessages.length > 0 && (
                <button className="update-profile-btn" onClick={handleUpdateProfileFromChat} disabled={loading}>
                  {tr.update_from_chat}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Step 3 */}
        {step === 3 && (
          <div className="step-container">
            <h2>{tr.step3_title}</h2>
            {showRefinement && (
              <div className="refinement-box">
                <div className="refinement-header">
                  <Sparkles size={20} />
                  <h3>{tr.refine_title}</h3>
                </div>
                <p className="refinement-hint">{tr.refine_hint}</p>
                <div className="refinement-input-container">
                  <input
                    type="text"
                    placeholder={tr.refine_placeholder}
                    value={refinementInput}
                    onChange={(e) => setRefinementInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleRefineSearch()}
                    disabled={loading}
                  />
                  <button onClick={handleRefineSearch} disabled={!refinementInput.trim() || loading} className="refine-btn">
                    {loading ? <Loader2 className="spinner" size={18} /> : tr.refine_btn}
                  </button>
                </div>
              </div>
            )}
            <div className="jobs-grid">
              {matches.map((job) => (
                <div key={job.id} className="job-card">
                  <div className="job-header">
                    <h3>{job.job_title}</h3>
                    <div className="match-score">{job.similarity}%</div>
                  </div>
                  <p className="company">🏢 {job.company}</p>
                  <p className="location">📍 {job.location}</p>
                  <p className="description">{job.description.substring(0, 200)}...</p>
                  <div className="job-actions">
                    {(job.application_url || job.url) && (
                      <a href={job.source === 'jobindex' ? (job.application_url || job.url) : job.url} target="_blank" rel="noopener noreferrer" className="link-btn">{tr.see_job}</a>
                    )}
                    <button className="primary-btn" onClick={() => handleGenerateLetter(job)} disabled={loading}>
                      {tr.write_application}
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {explanations && (
              <div className="explanations-box">
                <h3>{tr.why_these}</h3>
                <p style={{ whiteSpace: 'pre-line' }}>{explanations}</p>
              </div>
            )}
            <button className="secondary-btn" onClick={() => setStep(2)}>{tr.back_to_profile}</button>
          </div>
        )}

        {/* Step 4 */}
        {step === 4 && (
          <div className="step-container">
            <h2>{tr.step4_title}</h2>
            {selectedJob && (
              <div className="job-summary">
                <h3>{selectedJob.job_title}</h3>
                <p>{selectedJob.company} - {selectedJob.location}</p>
              </div>
            )}
            <div className="letter-container">
              <textarea className="letter-text" value={motivationLetter} onChange={(e) => setMotivationLetter(e.target.value)} rows={20} />
            </div>
            <div className="letter-actions">
              <button className="secondary-btn" onClick={() => setStep(3)}>{tr.back_to_jobs}</button>
              <button className="primary-btn" onClick={() => { navigator.clipboard.writeText(motivationLetter); alert(tr.copied); }}>
                <CheckCircle size={20} />
                {tr.copy_application}
              </button>
            </div>
          </div>
        )}
      </div>

      <footer className="app-footer">
        <div className="footer-links">
          <Link to="/privatlivspolitik">{tr.footer_privacy}</Link>
          <a href="mailto:sb@jobchat.dk">{tr.footer_contact}</a>
          <Link to="/om">{tr.footer_about}</Link>
        </div>
        <p>© 2026 jobchat.dk • CVR: 46335635</p>
      </footer>
    </div>
  );
}

// ─── Root with Router ─────────────────────────────────────────────────────────
function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<MainApp />} />
        <Route path="/privatlivspolitik" element={<PrivacyPage />} />
        <Route path="/om" element={<AboutPage />} />
      </Routes>
    </Router>
  );
}

export default App;
